import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layers, XCircle } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORY_LABELS = {
  eld: 'ELD', parking: 'Parking', insurance: 'Insurance', lease_on: 'Lease-on',
  trailer_rental: 'Trailer Rental', dispatch_fee: 'Dispatch Fee', factoring_fee: 'Factoring Fee',
  escrow: 'Escrow', legacy_balance: 'Prior Balance', toll: 'Toll', truck_wash: 'Truck Wash',
  repair: 'Repair', standing: 'Standing Deduction',
};

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  partial: 'bg-blue-100 text-blue-800 border-blue-200',
};

const DeferredDeductions = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending,partial');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/deferred-deductions?status=${statusFilter}`, { withCredentials: true });
      setRows(res.data || []);
    } catch (err) {
      toast.error('Failed to load deferred deductions');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const cancel = async (row) => {
    const reason = window.prompt(`Cancel outstanding ${CATEGORY_LABELS[row.category] || row.category} of ${fmtMoney(row.amount_outstanding)}? Optionally enter a reason:`, '');
    if (reason === null) return;
    try {
      await axios.patch(`${API}/deferred-deductions/${row.id}/cancel?reason=${encodeURIComponent(reason || '')}`, {}, { withCredentials: true });
      toast.success('Deferred deduction cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const categories = Array.from(new Set(rows.map((r) => r.category)));
  const filtered = categoryFilter === 'all' ? rows : rows.filter((r) => r.category === categoryFilter);
  const totalOutstanding = filtered.reduce((s, r) => s + (Number(r.amount_outstanding) || 0), 0);

  return (
    <DashboardLayout>
      <div data-testid="deferred-deductions-page" className="p-4 md:p-8 max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            <Layers className="w-8 h-8 text-[#002FA7]" /> Deferred Deductions
          </h1>
          <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Uncollected deductions carried forward across pay periods. They auto-collect on the next payroll run with enough earnings.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Status</label>
            <select data-testid="deferred-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md bg-white text-sm">
              <option value="pending,partial">Open (pending + partial)</option>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="collected">Collected</option>
              <option value="cancelled">Cancelled</option>
              <option value="pending,partial,collected,cancelled">All</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Category</label>
            <select data-testid="deferred-category-filter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md bg-white text-sm">
              <option value="all">All categories</option>
              {categories.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
            </select>
          </div>
          <div className="ml-auto text-right">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Total Outstanding</p>
            <p data-testid="deferred-total-outstanding" className="text-2xl font-bold text-orange-700" style={{ fontFamily: 'Work Sans, sans-serif' }}>{fmtMoney(totalOutstanding)}</p>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <div data-testid="deferred-empty" className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center">
            <Layers className="w-10 h-10 text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-700">No deferred deductions</p>
            <p className="text-xs text-slate-500 mt-1">Nothing is being carried forward right now.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-widest text-slate-500 bg-slate-50">
                  <th className="text-left py-3 px-4 font-semibold">Driver</th>
                  <th className="text-left py-3 px-4 font-semibold">Category</th>
                  <th className="text-left py-3 px-4 font-semibold">Since</th>
                  <th className="text-right py-3 px-4 font-semibold">Original</th>
                  <th className="text-right py-3 px-4 font-semibold">Collected</th>
                  <th className="text-right py-3 px-4 font-semibold">Outstanding</th>
                  <th className="text-center py-3 px-4 font-semibold">Status</th>
                  <th className="text-right py-3 px-4 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`deferred-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-slate-900 font-medium">{r.driver_name || r.driver_email || r.driver_id}</td>
                    <td className="py-3 px-4 text-slate-700">{CATEGORY_LABELS[r.category] || r.category}</td>
                    <td className="py-3 px-4 text-slate-500">{r.source_period_end || '—'}</td>
                    <td className="py-3 px-4 text-right text-slate-700">{fmtMoney(r.original_amount_due)}</td>
                    <td className="py-3 px-4 text-right text-emerald-700">{fmtMoney(r.amount_collected)}</td>
                    <td className="py-3 px-4 text-right text-orange-700 font-semibold">{fmtMoney(r.amount_outstanding)}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-block px-2 py-1 rounded text-[11px] font-semibold border ${STATUS_STYLES[r.status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{r.status}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {(r.status === 'pending' || r.status === 'partial') && (
                        <button data-testid={`deferred-cancel-${r.id}`} onClick={() => cancel(r)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 border border-red-200 rounded-md px-2.5 py-1.5 hover:bg-red-50">
                          <XCircle className="w-3.5 h-3.5" /> Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DeferredDeductions;
